/**
 * Supabase Client Configuration
 * Browser-side PKCE-enabled Supabase client for OAuth authentication
 */

import { createClient } from '@supabase/supabase-js';

/**
 * PKCE Helper Functions
 * Generate and manage PKCE code verifier/challenge for OAuth
 */

// Generate cryptographically secure random string for PKCE verifier
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// Generate PKCE code challenge from verifier using SHA256
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// Store PKCE verifier in localStorage with expiry
export function storePKCEVerifier(verifier: string): void {
  const expiresAt = Date.now() + (10 * 60 * 1000); // 10 minute expiry
  localStorage.setItem('pkce_verifier', JSON.stringify({
    verifier,
    expiresAt
  }));
}

// Retrieve and validate PKCE verifier from localStorage
export function retrievePKCEVerifier(): string | null {
  try {
    const stored = localStorage.getItem('pkce_verifier');
    if (!stored) return null;

    const { verifier, expiresAt } = JSON.parse(stored);
    if (Date.now() > expiresAt) {
      localStorage.removeItem('pkce_verifier');
      return null;
    }

    return verifier;
  } catch {
    localStorage.removeItem('pkce_verifier');
    return null;
  }
}

// Clear PKCE verifier after successful exchange
export function clearPKCEVerifier(): void {
  localStorage.removeItem('pkce_verifier');
}

// Environment variables for Supabase configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
}

// Create Supabase client with PKCE flow
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Enable PKCE flow type for secure OAuth
    flowType: 'pkce',
    // Enable session detection for OAuth callback handling
    detectSessionInUrl: true,
    // CRITICAL: Enable persistence for PKCE verifier storage
    // We'll clear the session after converting to cookies
    persistSession: true,
    // Disable auto-refresh - backend handles refresh via cookies
    autoRefreshToken: false,
  },
});

// Export types for TypeScript support
export type { User, Session, AuthError } from '@supabase/supabase-js';